<script setup>
  import { computed } from 'vue';
  import Icon from "@/components/icons/Icon.vue";
  import useUserTips from '@/composables/useUserTips';

  const props = defineProps({
    id: {
      type: [String, Number],
      required: true
    },
    floating: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: 'side'
    },
    alignment: {
      type: String,
      default: 'centered'
    },
    verticalAlignment: {
      type: String,
      default: 'centered'
    },
    adjustments: {
      type: [Object, String],
      required: false
    },
    action: {
      type: String,
      default: 'dismiss'
    },
    newTag: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['dismiss']);

  const { isDismissed, dismiss } = useUserTips();

  const showTip = computed(() => !isDismissed(props.id));

  const handleCloseClick = () => {
    dismiss(props.id, props.action);
    emit('dismiss');
  };
</script>
<template>
  <div
    v-if="showTip"
    class='jwx-v-user-tip'
    :data-type="props.floating ? 'floating' : 'block'"
    :data-position="props.floating ? props.position : null"
    :data-alignment="props.alignment"
    :data-vertical-alignment="props.position === 'side' ? props.verticalAlignment : null"
    :data-new-tag="props.newTag ? '' : null"
    :style="props.adjustments"
  >
    <div
      v-if="props.floating"
      class='jwx-v-user-tip__pointer'
    >
      <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        <path d="M290.5 51.8C283.3 39.5 270.2 32 256 32s-27.3 7.5-34.5 19.8l-216 368c-7.3 12.4-7.3 27.7-.2 40.1S25.7 480 40 480H472c14.3 0 27.6-7.7 34.7-20.1s7-27.8-.2-40.1l-216-368z"/>
      </svg>
    </div>
    <div class='jwx-v-user-tip__icon'>
      <slot name="icon">
        <Icon name="info" />
      </slot>
    </div>
    <div class='jwx-v-user-tip__content'>
      <header class='jwx-v-user-tip__header'>
        <p class='jwx-v-user-tip__header-text jwx-text-left'>
          <slot name="title"></slot>
        </p>
      </header>
      <div class='jwx-v-user-tip__body'>
        <p class="jwx-v-user-tip__text jwx-text-left">
          <slot name="message"></slot>
        </p>
      </div>
    </div>
    <button
      type='button'
      class='jwx-v-user-tip__close-button'
      @click="handleCloseClick"
    >
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>
        <path d='M175 175c9.4-9.3 24.6-9.3 33.1 0l47 47.1L303 175c9.4-9.3 24.6-9.3 33.1 0 10.2 9.4 10.2 24.6 0 33.1l-46.2 47 46.2 47.9c10.2 9.4 10.2 24.6 0 33.1-8.5 10.2-23.7 10.2-33.1 0l-47.9-46.2-47 46.2c-8.5 10.2-23.7 10.2-33.1 0-9.3-8.5-9.3-23.7 0-33.1l47.1-47.9-47.1-47c-9.3-8.5-9.3-23.7 0-33.1zm337 81c0 141.4-114.6 256-256 256S0 397.4 0 256 114.6 0 256 0s256 114.6 256 256zM256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48z' />
      </svg>
      <span class='sr-only'>Close this user tip.</span>
    </button>
  </div>
</template>
<style scoped lang="scss">
.jwx-v-user-tip {
  --jwx-user-tip-icon-width: 1.75rem;
  --jwx-user-tip-inline-icon-width: 1.125rem;
  --jwx-user-tip-close-button-width: 2.75rem;
  --jwx-user-tip-close-icon-width: 1.25rem;
  --jwx-user-tip-padding: .5rem;
  --jwx-user-tip-margin: 0;
  --jwx-user-tip-body-header-size: 1rem;
  --jwx-user-tip-body-text-size: 1rem;
  --jwx-user-tip-background-color: var(--jwx-surface, #f0f0f0);
  --jwx-user-tip-header-text-color: var(--jwx-text-color, #333333);
  --jwx-user-tip-body-text-color: var(--jwx-text-color, #666666);
  --jwx-user-tip-icon-color: var(--jwx-color-active, var(--jwx-user-tip-body-text-color));
  --jwx-user-tip-close-button-color: var(--jwx-icon-color, #999999);
  --jwx-user-tip-border-radius: 8px;
  --jwx-user-tip-shadow-color: 0deg 0% 30%;
  --jwx-user-tip-pointer-width: .75rem;
  --jwx-user-tip-pointer-offset: .25rem;
  --jwx-user-tip-offset: calc(var(--jwx-user-tip-pointer-width) - 2px);
  --jwx-user-tip-min-width: 25rem;
  --jwx-user-tip-max-width: 28rem;

  display: grid;
  grid-template:
    "icon content close" min-content
    / var(--jwx-user-tip-icon-width) 1fr var(--jwx-user-tip-close-button-width);
  gap: var(--jwx-user-tip-padding);
  margin: var(--jwx-user-tip-margin);
  padding: var(--jwx-user-tip-padding);
  background-color: var(--jwx-user-tip-background-color);
  border-radius: var(--jwx-user-tip-border-radius);
  box-shadow: inset 0 0 0 1px hsla(0, 0%, 75%, .125);
  color: var(--jwx-user-tip-body-text-color);
  z-index: 1;
}

.jwx-v-user-tip[data-new-tag] {
  position: relative;
}

.jwx-v-user-tip[data-new-tag]::before {
  content: 'NEW';
  position: absolute;
  top: 2px;
  display: flex;
  justify-content: center;
  padding: 2px 0;
  grid-area: icon;
  width: var(--jwx-user-tip-icon-width);
  background-color: #1C82E1;
  border-radius: 3px;
  font-size: .625rem;
  color: #ffffff;
}

.jwx-v-user-tip[data-new-tag] .jwx-v-user-tip__icon {
  margin-top: 1.125rem;
}

.jwx-v-user-tip[data-type="block"] {
  box-shadow:
    inset 0 0 0 1px hsla(0, 0%, 75%, .25),
    0.3px 0.5px 0.7px hsl(var(--jwx-user-tip-shadow-color) / .1),
    1.5px 2.9px 3.7px -0.4px hsl(var(--jwx-user-tip-shadow-color) / .125);

  &[data-alignment="centered"] {
    align-self: center;
    justify-self: center;
  }
}

.jwx-v-user-tip[data-type="floating"] {
  --jwx-user-tip-min-width: 18rem;
  --jwx-user-tip-max-width: 20rem;
  position: absolute;
  box-shadow:
    inset 0 0 0 1px hsla(0, 0%, 75%, .25),
    0.3px 0.5px 0.7px hsl(var(--jwx-user-tip-shadow-color) / .15),
    1.5px 2.9px 3.7px -0.4px hsl(var(--jwx-user-tip-shadow-color) / .15),
    2.7px 5.4px 6.8px -0.7px hsl(var(--jwx-user-tip-shadow-color) / .15),
    4.5px 8.9px 11.2px -1.1px hsl(var(--jwx-user-tip-shadow-color) / .15),
    7.1px 14.3px 18px -1.4px hsl(var(--jwx-user-tip-shadow-color) / .15),
    11.2px 22.3px 28.1px -1.8px hsl(var(--jwx-user-tip-shadow-color) / .15),
    17px 33.9px 42.7px -2.1px hsl(var(--jwx-user-tip-shadow-color) / .15),
    25px 50px 62.9px -2.5px hsl(var(--jwx-user-tip-shadow-color) / .15);
  min-width: var(--jwx-user-tip-min-width);
  max-width: var(--jwx-user-tip-max-width);
}

.jwx-v-user-tip[data-position="side"] {
  &[data-alignment="right"] { left: calc(100% + var(--jwx-user-tip-offset)); }
  &[data-alignment="left"] { right: calc(100% + var(--jwx-user-tip-offset)); }
  &[data-vertical-alignment="top"] { top: 0; }
  &[data-vertical-alignment="centered"] { top: 50%; transform: translateY(-50%); }
  &[data-vertical-alignment="bottom"] { top: 0; }
}

.jwx-v-user-tip[data-position="left"] {
  top: 0;
  right: calc(100% + var(--jwx-user-tip-offset));
}

.jwx-v-user-tip[data-position="top"],
.jwx-v-user-tip[data-position="bottom"] {
  &[data-alignment="right"] { right: calc(0% + var(--jwx-user-tip-offset)); }
  &[data-alignment="centered"] { left: 50%; transform: translateX(-50%); }
  &[data-alignment="left"] { left: calc(0px + var(--jwx-user-tip-offset)); }
}

.jwx-v-user-tip[data-position="bottom"] {
  top: calc(100% + var(--jwx-user-tip-pointer-width) + var(--jwx-user-tip-offset));
}

.jwx-v-user-tip[data-position="top"] {
  bottom: calc(100% + var(--jwx-user-tip-pointer-width) + var(--jwx-user-tip-offset));
}

.jwx-v-user-tip__icon {
  display: grid;
  place-content: center;

  // The default Icon fallback renders in this component's scope; a consumer's
  // #icon slot content needs :slotted() to be reached.
  & > svg,
  & > :slotted(svg) {
    width: var(--jwx-user-tip-icon-width);
    height: var(--jwx-user-tip-icon-width);
    fill: var(--jwx-user-tip-icon-color)
  }
}

.jwx-v-user-tip__header {
  margin-bottom: .2em;
}

.jwx-v-user-tip .jwx-v-user-tip__header-text {
  margin: 0;
  font-size: var(--jwx-user-tip-body-header-size);
  font-weight: 700;
  color: var(--jwx-user-tip-header-text-color);
}

.jwx-v-user-tip__body {
  display: flex;
}

.jwx-v-user-tip__text {
  margin: 0;
  font-size: var(--jwx-user-tip-body-text-size);
  line-height: 1.1;
  white-space: initial;
}

// Inline icons live in the message/title slots, so consumers' markup needs
// :slotted() to be reached by these scoped styles.
:slotted(.jwx-v-user-tip__inline-icon) > svg {
  width: var(--jwx-user-tip-inline-icon-width);
  height: var(--jwx-user-tip-inline-icon-width);
  fill: var(--jwx-user-tip-body-text-color);
  vertical-align: -3px;
}

.jwx-v-user-tip__close-button {
  grid-area: close;
  position: relative;
  display: grid;
  justify-content: end;
  padding: 0;
  background-color: transparent;
  border: none;
  outline: none;
  width: var(--jwx-user-tip-close-button-width);
  max-height: var(--jwx-user-tip-close-button-width);
  cursor: pointer;

  & > svg {
    width: var(--jwx-user-tip-close-icon-width);
    height: var(--jwx-user-tip-close-icon-width);
    fill: var(--jwx-user-tip-close-button-color);
    transition: all 300ms ease-in-out;
  }

  &:hover,
  &:focus {
    --jwx-user-tip-close-button-color: var(--jwx-user-tip-body-text-color);
  }

  &:after {
    content: '';
    position: absolute;
    width: 44px;
    height: 44px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.jwx-v-user-tip__pointer {
  --__rotate: 0deg;
  --__translateY: 0%;
  --__translateX: 0%;

  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: .75rem;
  height: .75rem;
  transform: translate(var(--__translateX), var(--__translateY)) rotate(var(--__rotate));

  & > svg {
    width: 100%;
    height: 100%;
    fill: var(--jwx-user-tip-close-button-color);
  }

  .jwx-v-user-tip[data-position="side"][data-alignment="left"] & { --__rotate: 90deg; left: calc(100% + 1px); }
  .jwx-v-user-tip[data-position="side"][data-alignment="right"] & { --__rotate: -90deg; right: calc(100% + 1px); }
  .jwx-v-user-tip[data-position="side"][data-vertical-alignment="top"] & { top: var(--jwx-user-tip-pointer-offset); }
  .jwx-v-user-tip[data-position="side"][data-vertical-alignment="centered"] & { --__translateY: -50%; top: 50%; }
  .jwx-v-user-tip[data-position="side"][data-vertical-alignment="bottom"] & { bottom: var(--jwx-user-tip-pointer-offset); }

  .jwx-v-user-tip[data-position="top"] & { top: calc(100% + 1px) }
  .jwx-v-user-tip[data-position="bottom"] & { bottom: calc(100% + 1px) }

  .jwx-v-user-tip[data-position="top"][data-alignment="left"] &,
  .jwx-v-user-tip[data-position="bottom"][data-alignment="left"] & {
    left: calc(0% + var(--jwx-user-tip-pointer-offset));
  }

  .jwx-v-user-tip[data-position="top"][data-alignment="centered"] &,
  .jwx-v-user-tip[data-position="bottom"][data-alignment="centered"] & {
    --__translateX: -50%; left: 50%;
  }

  .jwx-v-user-tip[data-position="top"][data-alignment="right"] &,
  .jwx-v-user-tip[data-position="bottom"][data-alignment="right"] & {
    right: calc(0% + var(--jwx-user-tip-pointer-offset));
  }
}
</style>
